import { defineComponent as _defineComponent } from 'vue'
import { getChildModel, makeFormModel, setState, batchSetState } from 'ah-common-lib/src/form/helpers';
import { ApiError } from 'ah-requests';
import { numberField, textField } from 'ah-common-lib/src/form/models/commonFields';
import { Beneficiary, FxMargins, getCurrencyPair, getPrimaryCcy, getSecondaryCcy } from 'ah-api-gateways';
import { TradeDetails } from '../../models/trade';
import TradePriceExchangeRate from '../info/TradePriceExchangeRate.vue';
import { QuotePriceRequest } from '../../requests/pricingRequest';
import { FormEvent, FormDefinition } from 'ah-common-lib/src/form/interfaces';
import PartnerProfitInfo from '../info/PartnerProfitInfo.vue';
import { computed, reactive, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useAhTradesState } from '../..';

import { Validation } from '@vuelidate/core';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClientRateAndMarginForm',
  props: {
    tradeDetails: { default: null },
    tradePrice: { default: null },
    beneficiary: null,
    peError: null
  },
  emits: ["set-markup", "update:validations"],
  setup(__props: any, { expose, emit }: { emit: ({
  /** Emits:
   * set-markup (payload: Partial<TradeDetails>) includes either markup or rate information, as provided by the user
   */
  (e: 'set-markup', payload: Partial<TradeDetails>): void;
  (e: 'update:validations', payload: Validation | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tradeDetails: TradeDetails | null, tradePrice: QuotePriceRequest | null, beneficiary?: Beneficiary, peError?: ApiError | null };





const state = reactive<{
  // Possible Pricing engine error to show (if relevant)
  peErrorText: string;
  calculating: boolean;
  clientSettings: FxMargins | null;
}>({
  peErrorText: '',
  calculating: false,
  clientSettings: null,
});

const clientRateAndMarginFormDef = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'clientRateAndMarginForm',
    fieldType: 'form',
    fields: [
      textField('clientRate', 'Client Rate', {
        fieldWrapperClass: 'col col-6 px-md-4 px-sm-2 text-center',
        required: false,
        defaultValue: undefined,
      }),
      numberField('clientRateMarkup', 'Markup', {
        fieldWrapperClass: 'col col-5 mb-2 px-md-4 px-sm-3 text-center',
        required: false,
        defaultValue: undefined,
        min: 0,
      }),
    ],
  }),
  validation: null,
});

const requestManager = useRequestManager({
  exposeToParent: ['getClientSettings'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'getClientSettings') {
      onClientChange();
    }
  },
});

const tradeState = useAhTradesState();

const isFormInvalid = computed(() => {
  if (clientRateAndMarginFormDef.validation) {
    return clientRateAndMarginFormDef.validation.$error;
  }
  return true;
});

const tradeCurrencyPair = computed(() => {
  if (props.tradePrice?.response) {
    return getCurrencyPair(props.tradePrice.response);
  }
  return '';
});

const clientRate = computed(() => {
  if (props.tradePrice?.response) {
    return getPrimaryCcy(props.tradePrice.response).clientRate.toFixed(
      tradeState.store.useSettingsStore().ratePrecision
    );
  }

  return 0;
});

const primaryLPRate = computed(() => {
  if (props.tradePrice?.response) {
    return getPrimaryCcy(props.tradePrice.response).productLpRate?.toFixed(
      tradeState.store.useSettingsStore().ratePrecision
    );
  }

  return 0;
});

const secondaryLPRate = computed(() => {
  if (props.tradePrice?.response) {
    return getSecondaryCcy(props.tradePrice.response).productLpRate?.toFixed(
      tradeState.store.useSettingsStore().ratePrecision
    );
  }

  return 0;
});

const onBehalfOfClient = useOnBehalfOf();

function onIsForwardChange() {
  if (state.clientSettings && !clientRateAndMarginFormDef.form.clientRateMarkup && props.tradeDetails?.timeFrame) {
    if (props.tradeDetails?.timeFrame?.isForward) {
      clientRateAndMarginFormDef.form.clientRateMarkup = state.clientSettings?.fxForwards.profitMargin;
    } else {
      clientRateAndMarginFormDef.form.clientRateMarkup = state.clientSettings?.fxSpots.profitMargin;
    }
  }
}
/**
 * Set defaults on trade details change
 */
watch(() => props.tradeDetails?.timeFrame?.isForward, onIsForwardChange, { immediate: true });

function onClientChange() {
  if (onBehalfOfClient.value) {
    requestManager.manager
      .sameOrCancelAndNew(
        'getClientSettings',
        tradeState.services.spreads.getClientSpreads(onBehalfOfClient.value.id),
        onBehalfOfClient.value.id
      )
      .subscribe((settings) => {
        state.clientSettings = settings;
        onIsForwardChange();
      });
  }
}

watch(() => onBehalfOfClient.value, onClientChange, { immediate: true });

/**
 * Set values on price change
 */
watch(
  () => props.tradePrice,
  () => {
    state.calculating = false;
    if (props.tradePrice?.response?.totalMarkupBasisPoints) {
      setErrorText();
      // FIXME using Math.round to avoid issues with decimal rounding in JS. remove once these are converted to int
      clientRateAndMarginFormDef.form.clientRateMarkup = Math.round(props.tradePrice.response.totalMarkupBasisPoints);
    }
    if (clientRate.value) {
      clientRateAndMarginFormDef.form.clientRate = clientRate.value;
    }
  },
  { immediate: true }
);

function setErrorText(errorText: string = '') {
  state.peErrorText = errorText || '';
  const clientMarkupField = getChildModel(clientRateAndMarginFormDef.form, 'clientRateMarkup')!;
  const clientRateField = getChildModel(clientRateAndMarginFormDef.form, 'clientRate')!;
  if (state.peErrorText) {
    const errors = [
      {
        name: 'peError',
        error: '',
      },
    ];
    setState(clientMarkupField, 'errors', errors);
    setState(clientRateField, 'errors', errors);
  } else {
    setState(clientMarkupField, 'errors', []);
    setState(clientRateField, 'errors', []);
  }
}

function formChange(formEvent: FormEvent) {
  if (formEvent.event === 'form-field-set-value') {
    const inputField = formEvent.model.$name;
    const calculatedField = formEvent.model.$name === 'clientRateMarkup' ? 'clientRate' : 'clientRateMarkup';
    state.calculating = true;

    batchSetState(clientRateAndMarginFormDef.form, 'placeholder', {
      [calculatedField]: 'Calculating...',
      [inputField]: '',
    });

    clientRateAndMarginFormDef.form[calculatedField] = '';

    emit('set-markup', { [inputField]: clientRateAndMarginFormDef.form[inputField] });
  }
  touch();
}

function calculatingFields(calculating = true) {
  state.calculating = calculating;
  if (calculating) {
    batchSetState(clientRateAndMarginFormDef.form, 'placeholder', ['clientRateMarkup', 'clientRate'], 'Calculating...');
  } else {
    batchSetState(clientRateAndMarginFormDef.form, 'placeholder', ['clientRateMarkup', 'clientRate'], '');
  }
}

function touch() {
  clientRateAndMarginFormDef.validation?.$touch();
}

watch(
  () => clientRateAndMarginFormDef.validation,
  () => {
    emit('update:validations', clientRateAndMarginFormDef.validation);
  },
  { immediate: true, deep: true }
);

expose({ isFormInvalid, calculatingFields, touch });

return { __sfc: true,emit, props, state, clientRateAndMarginFormDef, requestManager, tradeState, isFormInvalid, tradeCurrencyPair, clientRate, primaryLPRate, secondaryLPRate, onBehalfOfClient, onIsForwardChange, onClientChange, setErrorText, formChange, calculatingFields, touch, TradePriceExchangeRate, PartnerProfitInfo }
}

})